import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag } from 'lucide-react';
import { PurchaseButton } from '@/components/CheckoutModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MerchandisePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/merchandise`).then(res => { setItems(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen pt-24" data-testid="merchandise-page">
      {/* Hero */}
      <section className="py-12 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 animated-gradient opacity-20" />
        <div className="container-custom relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cta/20 text-cta mb-6">
              <ShoppingBag className="w-4 h-4" />
              <span className="text-sm font-medium">Official Merchandise</span>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-serif font-bold text-white mb-6" data-testid="merchandise-title">
              Matrix University Merch
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground">
              Rep The Matrix University. Support immersive education.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products */}
      <section className="py-12 md:py-16">
        <div className="container-custom">
          {loading ? (
            <div className="max-w-md mx-auto rounded-2xl glass-light animate-pulse h-96" />
          ) : items.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {items.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="rounded-2xl glass-light overflow-hidden"
                  data-testid={`merch-card-${item.id}`}
                >
                  {/* Product Image */}
                  <div className="aspect-square bg-black relative overflow-hidden">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.title} className="w-full h-full object-contain" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-cta/20">
                        <ShoppingBag className="w-16 h-16 text-cta/60" />
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-serif font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{item.description}</p>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-bold text-cta">{item.price}</span>
                      {item.in_stock && <span className="text-green-400 text-sm font-medium">In Stock</span>}
                    </div>
                    <PurchaseButton
                      item={item}
                      itemType="merchandise"
                      className="w-full flex items-center justify-center gap-2"
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <ShoppingBag className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground">Merchandise coming soon!</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
